/**
 * Focus — the owner plans their own work.
 *
 * Goals ranked by drag (rank = priority) with an audience (me / AI team /
 * both), a weekly budget and a next step; a Mon–Sun grid where the owner
 * draws work blocks over their Outlook week; the pomodoro timer. Nothing
 * reaches the AI team until "Inform <chief of staff>" commits the changes.
 *
 * URL state: `?view=today|week|goals` (default today), `?week=YYYY-MM-DD`
 * (any day of the shown week; absent = this week). Live updates arrive
 * through `FocusTimerChip`'s single `focus:<owner>` subscription, which
 * invalidates everything under `["focus"]`.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { clsx } from "clsx";

import { apiErrorCode, apiErrorDetail } from "../api/client";
import {
  FOCUS_KEYS,
  focusApi,
  type BlockPatchBody,
  type CommitResult,
  type FocusBlock,
  type FocusGoal,
  type GoalCreateBody,
  type GoalPatchBody,
  type GoalsPayload,
  type TimerSnapshot,
  type WeekPayload,
} from "../api/focus";
import { useToast } from "../components/toast/context";
import { BlockEditorModal, type BlockDraft, type BlockEditorTarget } from "../components/focus/BlockEditorModal";
import { CommitBar } from "../components/focus/CommitBar";
import { CommitModal } from "../components/focus/CommitModal";
import { GoalList } from "../components/focus/GoalList";
import { TimerControls } from "../components/focus/TimerControls";
import { TimerFace } from "../components/focus/TimerFace";
import { TodayAgenda } from "../components/focus/TodayAgenda";
import { WeekBudgetPanel } from "../components/focus/WeekBudgetPanel";
import { WeekGrid } from "../components/focus/WeekGrid";
import { WeekToolbar } from "../components/focus/WeekToolbar";
import { PageHeader } from "../components/PageHeader";
import { QueryBoundary } from "../components/QueryBoundary";
import {
  FOCUS_VIEWS,
  allDayCovers,
  budgetRows,
  chiefOfStaffName,
  defaultStartGoal,
  isBreak,
  type FocusView,
} from "../lib/focus";
import {
  berlinDate,
  berlinToUtc,
  formatDayLong,
  formatMinutes,
  spanOnDay,
  toBerlinIso,
  todayBerlin,
  weekStartOf,
} from "../lib/focusTime";
import { useFocusTimer, useTimerQuery } from "../lib/hooks/useFocusTimer";
import { useNow } from "../lib/hooks/useNow";

/** Outlook moves arrive only through a read of the week — refresh now and then. */
const WEEK_POLL_MS = 5 * 60_000;
const FULL_DAY_KEY = "dux-focus:grid-full-day";
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIMER_RELOAD_CODES = new Set(["stale_session", "focus_running", "phase_mismatch"]);

function readFullDay(): boolean {
  try {
    return window.localStorage.getItem(FULL_DAY_KEY) === "1";
  } catch {
    return false;
  }
}

function spanIso(date: string, startMin: number, endMin: number) {
  return {
    starts_at: toBerlinIso(berlinToUtc(date, startMin)),
    ends_at: toBerlinIso(berlinToUtc(date, endMin)),
  };
}

export function FocusPage() {
  const [sp] = useSearchParams();
  const viewParam = sp.get("view");
  const view: FocusView = FOCUS_VIEWS.some((v) => v.value === viewParam) ? (viewParam as FocusView) : "today";
  const weekParam = sp.get("week");

  const nowMs = useNow(30_000);
  const today = todayBerlin(nowMs);
  const currentWeek = weekStartOf(today);
  const shownWeek = weekParam && DATE_RE.test(weekParam) ? weekStartOf(weekParam) : currentWeek;
  // Today always reads the current week; the Week view reads the one in the URL.
  const weekStart = view === "week" ? shownWeek : currentWeek;
  const weekKey = useMemo(() => FOCUS_KEYS.week(weekStart), [weekStart]);

  const qc = useQueryClient();
  const toast = useToast();

  const week = useQuery({
    queryKey: weekKey,
    queryFn: () => focusApi.week(weekStart),
    enabled: view !== "goals",
    refetchInterval: WEEK_POLL_MS,
    placeholderData: keepPreviousData,
  });
  const goals = useQuery({
    queryKey: FOCUS_KEYS.goals("all"),
    queryFn: () => focusApi.goals("all"),
    enabled: view === "goals",
  });
  const seats = useQuery({
    queryKey: FOCUS_KEYS.seats,
    queryFn: focusApi.seats,
    enabled: view === "goals",
    staleTime: 5 * 60_000,
  });
  const changes = useQuery({ queryKey: FOCUS_KEYS.changes, queryFn: focusApi.changes });
  const commits = useQuery({ queryKey: FOCUS_KEYS.commits, queryFn: () => focusApi.commits(5) });
  const timerQuery = useTimerQuery();
  const timer = timerQuery.data;

  const afterPlanWrite = useCallback(() => {
    void qc.invalidateQueries({ queryKey: FOCUS_KEYS.weeks });
    void qc.invalidateQueries({ queryKey: FOCUS_KEYS.changes });
    void qc.invalidateQueries({ queryKey: ["focus", "goals"] });
  }, [qc]);

  // ---------------------------------------------------------------- blocks

  /** Rewrite one cached block (null drops it); returns the rollback. */
  const patchCachedBlock = useCallback(
    (blockId: string, change: (b: FocusBlock) => FocusBlock | null): (() => void) => {
      // No revert: a cancelled refetch must not restore its pre-fetch data
      // over the optimistic write that follows.
      void qc.cancelQueries({ queryKey: weekKey }, { revert: false });
      const prev = qc.getQueryData<WeekPayload>(weekKey);
      if (prev) {
        qc.setQueryData<WeekPayload>(weekKey, {
          ...prev,
          blocks: prev.blocks.flatMap((b) => {
            if (b.id !== blockId) return [b];
            const next = change(b);
            return next ? [next] : [];
          }),
        });
      }
      return () => {
        if (prev) qc.setQueryData(weekKey, prev);
      };
    },
    [qc, weekKey],
  );

  const [editor, setEditor] = useState<{ key: number; target: BlockEditorTarget } | null>(null);
  const editorSeq = useRef(0);
  const openEditor = useCallback((target: BlockEditorTarget) => {
    editorSeq.current += 1;
    setEditor({ key: editorSeq.current, target });
  }, []);

  const createBlock = useMutation({
    mutationFn: focusApi.createBlock,
    onSuccess: (block) => {
      const overlap = block.warnings?.find((w) => w.kind === "outlook_overlap");
      if (overlap) toast.error(`Planned over “${overlap.subject ?? "a meeting"}” in Outlook`);
      setEditor(null);
    },
    onError: (err) => toast.error(err),
    onSettled: afterPlanWrite,
  });

  const patchBlock = useMutation({
    mutationFn: ({ block, patch }: { block: FocusBlock; patch: BlockPatchBody; optimistic?: boolean }) =>
      focusApi.patchBlock(block.id, patch),
    onMutate: ({ block, patch, optimistic }) => {
      if (!optimistic) return undefined;
      return patchCachedBlock(block.id, (b) => ({
        ...b,
        starts_at: patch.starts_at ?? b.starts_at,
        ends_at: patch.ends_at ?? b.ends_at,
        minutes: Math.round(
          (Date.parse(patch.ends_at ?? b.ends_at) - Date.parse(patch.starts_at ?? b.starts_at)) / 60_000,
        ),
      }));
    },
    onSuccess: (saved) => {
      patchCachedBlock(saved.id, () => saved);
      setEditor((e) => (e?.target.kind === "edit" && e.target.block.id === saved.id ? null : e));
    },
    onError: (err, _vars, rollback) => {
      rollback?.();
      toast.error(err);
    },
    onSettled: afterPlanWrite,
  });

  const deleteBlock = useMutation({
    mutationFn: (block: FocusBlock) => focusApi.deleteBlock(block.id),
    onMutate: (block) => patchCachedBlock(block.id, () => null),
    onSuccess: () => setEditor(null),
    onError: (err, _block, rollback) => {
      rollback?.();
      toast.error(err);
    },
    onSettled: afterPlanWrite,
  });

  const syncBlock = useMutation({
    mutationFn: (block: FocusBlock) => focusApi.syncBlock(block.id),
    onSuccess: (saved) => {
      patchCachedBlock(saved.id, () => saved);
      setEditor((e) => (e?.target.kind === "edit" && e.target.block.id === saved.id ? { ...e, target: { kind: "edit", block: saved } } : e));
      if (saved.calendar.state === "synced") toast.ok("In Outlook again");
      else toast.error("Still not in Outlook");
    },
    onError: (err) => toast.error(err),
  });

  const copyWeek = useMutation({
    mutationFn: ({ from, to }: { from: string; to: string }) => focusApi.copyWeek(from, to),
    onSuccess: (res) => toast.ok(`Copied ${res.created.length} · ${res.skipped.length} skipped`),
    onError: (err) => toast.error(err),
    onSettled: afterPlanWrite,
  });

  const [refreshing, setRefreshing] = useState(false);
  async function refreshOutlook() {
    setRefreshing(true);
    try {
      await qc.fetchQuery({ queryKey: weekKey, queryFn: () => focusApi.week(weekStart, true), staleTime: 0 });
    } catch (err) {
      toast.error(err);
    } finally {
      setRefreshing(false);
    }
  }

  // ----------------------------------------------------------------- timer

  const [timerBusy, setTimerBusy] = useState(false);
  async function runTimer(action: () => Promise<TimerSnapshot>, done?: string): Promise<void> {
    setTimerBusy(true);
    try {
      qc.setQueryData(FOCUS_KEYS.timer, await action());
      if (done) toast.ok(done);
    } catch (err) {
      toast.error(err);
      if (TIMER_RELOAD_CODES.has(apiErrorCode(err) ?? "")) {
        void qc.invalidateQueries({ queryKey: FOCUS_KEYS.timer });
      }
      throw err;
    } finally {
      setTimerBusy(false);
    }
  }
  const expectedSession = timer && isBreak(timer.phase) ? timer.session?.id : undefined;
  const startGoal = (goalId: string) =>
    void runTimer(() => focusApi.start({ goal_id: goalId, expected_session_id: expectedSession })).catch(
      () => undefined,
    );
  const startBlock = (block: FocusBlock) =>
    void runTimer(() => focusApi.start({ block_id: block.id, expected_session_id: expectedSession })).catch(
      () => undefined,
    );
  const abortFocus = (sessionId: string) =>
    void runTimer(
      () => focusApi.stop({ mode: "abort", expected_session_id: sessionId }),
      "Focus aborted – it counts nothing",
    ).catch(() => undefined);
  const skipBreak = (sessionId: string) =>
    void runTimer(() => focusApi.stop({ mode: "skip", expected_session_id: sessionId })).catch(() => undefined);
  const noteFocus = (note: string, sessionId: string | undefined) =>
    runTimer(() => focusApi.note(note, sessionId), "Note saved");

  // ----------------------------------------------------------------- goals

  const [goalBusyId, setGoalBusyId] = useState<string | null>(null);
  const createRequestId = useRef<string | null>(null);
  const createGoal = useMutation({
    mutationFn: (body: GoalCreateBody) => focusApi.createGoal(body),
    onSuccess: () => {
      createRequestId.current = null;
    },
    onError: (err) => toast.error(err),
    onSettled: afterPlanWrite,
  });
  async function onCreateGoal(body: Omit<GoalCreateBody, "request_id">): Promise<void> {
    // One id per goal being created: a retry after a lost response replays
    // the first write instead of creating the goal twice.
    createRequestId.current ??= crypto.randomUUID();
    await createGoal.mutateAsync({ ...body, request_id: createRequestId.current });
  }

  async function onPatchGoal(goal: FocusGoal, patch: GoalPatchBody): Promise<FocusGoal> {
    setGoalBusyId(goal.id);
    try {
      const saved = await focusApi.patchGoal(goal.id, patch);
      qc.setQueryData<GoalsPayload>(FOCUS_KEYS.goals("all"), (prev) =>
        prev ? { ...prev, goals: prev.goals.map((g) => (g.id === saved.id ? saved : g)) } : prev,
      );
      return saved;
    } catch (err) {
      const message = apiErrorDetail(err);
      toast.error(message);
      throw new Error(message);
    } finally {
      setGoalBusyId(null);
      afterPlanWrite();
    }
  }

  async function onReorder(goalIds: string[]) {
    const key = FOCUS_KEYS.goals("all");
    void qc.cancelQueries({ queryKey: key }, { revert: false });
    const prev = qc.getQueryData<GoalsPayload>(key);
    if (prev) {
      // The open goals keep their rank slots; only their order inside changes.
      const slots = prev.goals
        .filter((g) => goalIds.includes(g.id))
        .map((g) => g.rank)
        .sort((a, b) => (a ?? Number.MAX_SAFE_INTEGER) - (b ?? Number.MAX_SAFE_INTEGER));
      const rankOf = new Map(goalIds.map((id, i) => [id, slots[i] ?? i + 1]));
      qc.setQueryData<GoalsPayload>(key, {
        ...prev,
        goals: prev.goals.map((g) => (rankOf.has(g.id) ? { ...g, rank: rankOf.get(g.id) ?? g.rank } : g)),
      });
    }
    try {
      const res = await focusApi.reorder(goalIds);
      if (res.team_order_changed) {
        toast.ok(`The team's priorities changed – inform ${chiefOfStaffName(changes.data)} when ready`);
      }
    } catch (err) {
      if (prev) qc.setQueryData(key, prev);
      toast.error(err);
    } finally {
      afterPlanWrite();
    }
  }

  // ---------------------------------------------------------------- commit

  const [commitOpen, setCommitOpen] = useState(false);
  const commitRequestId = useRef<string | null>(null);
  async function onCommit(note: string): Promise<void> {
    commitRequestId.current ??= crypto.randomUUID();
    let res: CommitResult;
    try {
      res = await focusApi.commit(commitRequestId.current, note);
    } catch (err) {
      toast.error(err);
      throw err;
    }
    commitRequestId.current = null;
    void qc.invalidateQueries({ queryKey: FOCUS_KEYS.changes });
    void qc.invalidateQueries({ queryKey: FOCUS_KEYS.commits });
    if (!res.committed) {
      toast.ok("Nothing to commit");
      setCommitOpen(false);
      return;
    }
    const { commit } = res;
    const n = commit.change_count;
    toast.show(
      commit.outcome === "informed"
        ? `${commit.informed?.name ?? commit.informed?.slug ?? "Chief of staff"} informed – ${n} ${n === 1 ? "change" : "changes"}`
        : commit.outcome === "no_cos"
          ? "No chief of staff configured – nothing sent"
          : "More than one chief of staff – nothing sent",
      { tone: commit.outcome === "informed" ? "ok" : "error" },
    );
    setCommitOpen(false);
  }

  // ------------------------------------------------------------ week view

  const [fullDay, setFullDay] = useState(readFullDay);
  function toggleFullDay() {
    const next = !fullDay;
    setFullDay(next);
    try {
      window.localStorage.setItem(FULL_DAY_KEY, next ? "1" : "0");
    } catch {
      // Not persisted — the toggle still applies to this visit.
    }
  }

  const [armedId, setArmedId] = useState<string | null>(null);
  const weekData = week.data;
  const plannableGoals = useMemo(
    () =>
      [...(weekData?.goals ?? [])]
        .filter((g) => g.plannable)
        .sort((a, b) => (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER)),
    [weekData],
  );
  const armedGoal =
    plannableGoals.find((g) => g.id === armedId) ?? plannableGoals[0] ?? null;

  const createBlockMutate = createBlock.mutate;
  const patchBlockMutate = patchBlock.mutate;
  const onDraw = useCallback(
    (date: string, startMin: number, endMin: number) => {
      if (!armedGoal) return;
      createBlockMutate({ goal_id: armedGoal.id, ...spanIso(date, startMin, endMin), protection: "soft" });
    },
    [armedGoal, createBlockMutate],
  );
  const onSlot = useCallback(
    (date: string, startMin: number, endMin: number) =>
      openEditor({ kind: "create", date, startMin, endMin, goalId: armedGoal?.id ?? null }),
    [armedGoal, openEditor],
  );
  const onMove = useCallback(
    (block: FocusBlock, date: string, startMin: number, endMin: number) =>
      patchBlockMutate({
        block,
        patch: { ...spanIso(date, startMin, endMin), expected_revision: block.revision },
        optimistic: true,
      }),
    [patchBlockMutate],
  );
  const onResize = useCallback(
    (block: FocusBlock, endMin: number) => {
      const date = berlinDate(Date.parse(block.starts_at));
      patchBlockMutate({
        block,
        patch: { ends_at: toBerlinIso(berlinToUtc(date, endMin)), expected_revision: block.revision },
        optimistic: true,
      });
    },
    [patchBlockMutate],
  );
  const onOpenBlock = useCallback((block: FocusBlock) => openEditor({ kind: "edit", block }), [openEditor]);

  function weekHref(start: string | null): string {
    const next = new URLSearchParams(sp);
    next.set("view", "week");
    if (start && start !== currentWeek) next.set("week", start);
    else next.delete("week");
    return `?${next.toString()}`;
  }

  function viewHref(value: FocusView): string {
    const next = new URLSearchParams(sp);
    next.set("view", value);
    return `?${next.toString()}`;
  }

  const editorBusy = createBlock.isPending || patchBlock.isPending || deleteBlock.isPending || syncBlock.isPending;
  const name = chiefOfStaffName(changes.data);

  return (
    <div className="page">
      <PageHeader
        title="Focus"
        description={`Your goals in priority order, the week you plan for them, and the timer that tracks the work. Changes stay yours until you inform ${name}.`}
        actions={
          <CommitBar
            pending={changes.data}
            lastCommit={commits.data?.[0]}
            onOpen={() => setCommitOpen(true)}
          />
        }
      />

      <nav className="subnav" aria-label="Focus views">
        {FOCUS_VIEWS.map((v) => (
          <Link key={v.value} to={viewHref(v.value)} className={clsx(view === v.value && "is-active")}>
            {v.label}
          </Link>
        ))}
      </nav>

      {view === "today" ? (
        <QueryBoundary query={week}>
          {(data) => {
            const todays = data.blocks.filter((b) =>
              spanOnDay(Date.parse(b.starts_at), Date.parse(b.ends_at), today),
            );
            const events = data.outlook.events.filter((e) =>
              e.all_day
                ? allDayCovers(e, today)
                : e.starts_at && e.ends_at && spanOnDay(Date.parse(e.starts_at), Date.parse(e.ends_at), today),
            );
            const plannedToday = todays
              .filter((b) => b.status === "planned")
              .reduce((sum, b) => sum + b.minutes, 0);
            return (
              <div className="focus-today">
                <section className="panel">
                  <TimerPanel
                    goals={plannableGoals}
                    busy={timerBusy}
                    onStart={startGoal}
                    onAbort={abortFocus}
                    onSkip={skipBreak}
                    onNote={noteFocus}
                  />
                </section>
                <section className="panel panel--flush">
                  <div className="panel-header">
                    <h3>{formatDayLong(today)}</h3>
                    <span className="text-xs text-ink-low font-mono">
                      {formatMinutes(plannedToday)} planned · {events.filter((e) => !e.all_day).length} meetings
                    </span>
                  </div>
                  <div className="panel-body">
                    <TodayAgenda
                      blocks={todays}
                      events={events}
                      nowMs={nowMs}
                      canStart={!!timer && timer.phase !== "focus" && !timerBusy}
                      plannableGoalIds={new Set(plannableGoals.map((g) => g.id))}
                      activeBlockId={timer?.phase === "focus" ? (timer.session?.block_id ?? null) : null}
                      onStartBlock={startBlock}
                      onOpenBlock={onOpenBlock}
                    />
                  </div>
                </section>
              </div>
            );
          }}
        </QueryBoundary>
      ) : null}

      {view === "week" ? (
        <QueryBoundary query={week}>
          {(data) => (
            <>
              <WeekToolbar
                week={data.week}
                isCurrent={data.week.start_date === currentWeek}
                hrefFor={weekHref}
                outlookState={data.outlook.state}
                outlookMailbox={data.outlook.mailbox}
                plannedBlocks={data.blocks.filter((b) => b.status === "planned").length}
                copying={copyWeek.isPending}
                refreshing={refreshing}
                fullDay={fullDay}
                onCopyLastWeek={() => copyWeek.mutate({ from: data.week.prev_start, to: data.week.start_date })}
                onRefresh={() => void refreshOutlook()}
                onToggleFullDay={toggleFullDay}
              />
              <div className="week-layout">
                <div className="panel panel--flush week-panel">
                  <WeekGrid
                    days={data.week.days}
                    blocks={data.blocks}
                    events={data.outlook.events}
                    nowMs={nowMs}
                    fullDay={fullDay}
                    armedGoal={armedGoal ? { id: armedGoal.id, title: armedGoal.title } : null}
                    onDraw={onDraw}
                    onSlot={onSlot}
                    onMove={onMove}
                    onResize={onResize}
                    onOpen={onOpenBlock}
                  />
                </div>
                <aside className="space-y-4">
                  <WeekBudgetPanel
                    rows={budgetRows(data.goals)}
                    totals={data.totals}
                    armedGoalId={armedGoal?.id ?? null}
                    onArm={setArmedId}
                  />
                  <WeekLegend />
                </aside>
              </div>
            </>
          )}
        </QueryBoundary>
      ) : null}

      {view === "goals" ? (
        <QueryBoundary query={goals}>
          {(data) => {
            const byRank = (a: FocusGoal, b: FocusGoal) =>
              (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER);
            const open = data.goals.filter((g) => g.status === "active" || g.status === "paused").sort(byRank);
            const archived = data.goals.filter((g) => g.status === "achieved" || g.status === "retired");
            return (
              <GoalList
                open={open}
                archived={archived}
                seats={seats.data ?? []}
                busyId={goalBusyId}
                creating={createGoal.isPending}
                onCreate={onCreateGoal}
                onPatch={onPatchGoal}
                onReorder={(ids) => void onReorder(ids)}
              />
            );
          }}
        </QueryBoundary>
      ) : null}

      {editor && weekData ? (
        <BlockEditorModal
          key={editor.key}
          target={editor.target}
          goals={plannableGoals.map((g) => ({ id: g.id, title: g.title }))}
          days={weekData.week.days}
          nowMs={nowMs}
          busy={editorBusy}
          onCreate={(draft: BlockDraft) => createBlock.mutate(draft)}
          onSave={(block, patch) => patchBlock.mutate({ block, patch })}
          onDelete={(block) => deleteBlock.mutate(block)}
          onRetrySync={(block) => syncBlock.mutate(block)}
          onRestore={(block) =>
            patchBlock.mutate({ block, patch: { restore: true, expected_revision: block.revision } })
          }
          onClose={() => setEditor(null)}
        />
      ) : null}

      <CommitModal
        open={commitOpen}
        pending={changes.data}
        lastCommit={commits.data?.[0]}
        onCommit={onCommit}
        onClose={() => setCommitOpen(false)}
      />
    </div>
  );
}

/**
 * The face + controls. Its own component so the 500 ms countdown clock
 * re-renders this panel only, not the whole page.
 */
function TimerPanel({
  goals,
  busy,
  onStart,
  onAbort,
  onSkip,
  onNote,
}: {
  goals: { id: string; title: string; rank: number | null; plannable: boolean }[];
  busy: boolean;
  onStart: (goalId: string) => void;
  onAbort: (sessionId: string) => void;
  onSkip: (sessionId: string) => void;
  onNote: (note: string, sessionId: string | undefined) => Promise<void>;
}) {
  const timer = useFocusTimer();
  const { snapshot } = timer;
  if (timer.unavailable) {
    return <p className="text-sm text-ink-low">The timer isn't available on this server.</p>;
  }
  if (!snapshot) {
    return (
      <div className="py-10 text-center text-ink-low text-sm">
        <span className="inline-block animate-pulse">Loading…</span>
      </div>
    );
  }
  return (
    <>
      <TimerFace snapshot={snapshot} remainingMs={timer.remainingMs} wrappingUp={timer.wrappingUp} />
      <TimerControls
        snapshot={snapshot}
        goals={goals}
        defaultGoalId={defaultStartGoal(snapshot, goals)}
        busy={busy}
        onStart={onStart}
        onAbort={onAbort}
        onSkip={onSkip}
        onNote={onNote}
      />
    </>
  );
}

function WeekLegend() {
  return (
    <div className="panel week-legend">
      <div className="week-legend-row">
        <span className="week-legend-swatch week-block--soft" /> soft – tentative in Outlook
      </div>
      <div className="week-legend-row">
        <span className="week-legend-swatch week-block--protected" /> protected – busy in Outlook
      </div>
      <div className="week-legend-row">
        <span className="week-legend-swatch week-event" /> Outlook meeting (read-only)
      </div>
      <div className="week-legend-row font-mono">○ pending · · synced · ! sync error</div>
      <p className="week-legend-hint">
        Drag on empty space to plan the armed goal, click for the editor. Drag a block to move it, its bottom edge
        to resize.
      </p>
    </div>
  );
}
